import { useState, useEffect, useCallback } from 'react'
import type { PointerEvent as ReactPointerEvent, CSSProperties } from 'react'

// Pointer-based drag-to-reorder, extracted from ColumnManager (E-CV-201) so every SaaS-admin
// builder reorders the same way ASP's column manager does. Native HTML5 `dragstart` silently
// refuses to fire in nested/overflow layouts — the admin builder panels are exactly that — while
// pointer events always deliver. Reorder is KEY-based (not index-based) so indices shifting
// mid-drag can't corrupt the order. Callers keep their up/down arrow buttons for keyboard a11y.
//
// Usage:
//   const keyOf = (item) => item.id
//   const { isDragging, rowProps, handleProps } = usePointerReorder((from, to) =>
//     setItems(a => reorderByKey(a, keyOf, from, to)))
//   // on each row wrapper: {...rowProps(keyOf(item))}
//   // on a grip element inside the row: <span {...handleProps(keyOf(item))}><GripVertical/></span>
export function usePointerReorder(onReorder: (fromKey: string, toKey: string) => void) {
  const [dragging, setDragging] = useState<string | null>(null)

  useEffect(() => {
    if (!dragging) return
    const stop = () => setDragging(null)
    window.addEventListener('pointerup', stop)
    window.addEventListener('pointercancel', stop)
    return () => { window.removeEventListener('pointerup', stop); window.removeEventListener('pointercancel', stop) }
  }, [dragging])

  const reorderTo = useCallback((src: string, target: string) => {
    if (src && target && src !== target) onReorder(src, target)
  }, [onReorder])

  const isDragging = (key: string) => dragging === key

  // Spread onto each reorderable row wrapper.
  const rowProps = (key: string) => ({
    onPointerEnter: () => { if (dragging && dragging !== key) reorderTo(dragging, key) },
  })

  // Spread onto the grip handle inside each row.
  const handleProps = (key: string): { onPointerDown: (e: ReactPointerEvent) => void; style: CSSProperties; title: string } => ({
    onPointerDown: (e: ReactPointerEvent) => { e.preventDefault(); setDragging(key) },
    style: { cursor: dragging ? 'grabbing' : 'grab', touchAction: 'none', display: 'inline-flex', padding: 2 },
    title: 'Drag to reorder',
  })

  return { dragging, isDragging, rowProps, handleProps }
}

// Immutably move the element whose key === fromKey to the position of toKey.
export function reorderByKey<T>(arr: T[], keyOf: (t: T) => string, fromKey: string, toKey: string): T[] {
  const from = arr.findIndex(x => keyOf(x) === fromKey)
  const to = arr.findIndex(x => keyOf(x) === toKey)
  if (from < 0 || to < 0 || from === to) return arr
  const next = [...arr]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}
